import { useEffect, useState } from 'react';
import { apiErrorMessage, apiFetch, v1Path } from '../../lib/api';
import { isMemberDto, parseItems } from '../../lib/work-types';
import { useSession } from '../session-context';

type ProjectAdminState = {
  isProjectAdmin: boolean;
  loading: boolean;
  error: string | null;
};

/**
 * Project settings writes (projects.update, states.write, labels.write) are granted
 * by ProjectMember.role === 'admin' only — see the grant table in middleware/authz.ts.
 */
export function useProjectAdmin(projectId: string): ProjectAdminState {
  const { email, ready } = useSession();
  const [state, setState] = useState<ProjectAdminState>({
    isProjectAdmin: false,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    setState({ isProjectAdmin: false, loading: true, error: null });

    apiFetch<unknown>(v1Path(`/projects/${projectId}/members`))
      .then((res) => {
        if (cancelled) return;
        const members = parseItems(res, isMemberDto, 'project members');
        const self = members.find((member) => member.email === email);
        setState({
          isProjectAdmin: self?.role === 'admin' && self.is_active,
          loading: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          isProjectAdmin: false,
          loading: false,
          error: apiErrorMessage(err, 'Failed to determine your stream role'),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, email, ready]);

  return state;
}
